'use client';

/**
 * Tenant Context
 * 
 * Provides tenant configuration to all components in the tree.
 * This is the primary way components access tenant-specific data.
 * 
 * Usage:
 * ```tsx
 * // In a Server Component (layout)
 * <TenantProvider value={tenantContext}>
 *   {children}
 * </TenantProvider>
 * 
 * // In a Client Component
 * const { organisationId, siteConfig, theme } = useTenant();
 * ```
 */

import type { SiteConfig } from '@/lib/api/clients/sites';
import type { WhatsAppConfig } from '@/lib/api/types';
import type { Currency, Locale, ThemeId } from '@/lib/config/app.config';
import type { ThemeConfig } from '@routeroyal/theme-core';
import {
    createContext,
    useContext,
    useMemo,
    type ReactNode,
} from 'react';

/**
 * Tenant context value type
 */
export interface TenantContextValue {
  /** Organisation ID from Route Royal */
  organisationId: string;
  
  /** Site ID from Route Royal */
  siteId: string;
  
  /** Full site configuration from Route Royal API */
  siteConfig: SiteConfig;
  
  /** Resolved theme object */
  theme: ThemeConfig;
  
  /** Theme ID string */
  themeId: ThemeId;
  
  /** Current locale */
  locale: Locale;
  
  /** Default locale for this tenant (used for x-default hreflang) */
  defaultLocale: Locale;
  
  /** Supported locales for this tenant */
  supportedLocales: Locale[];
  
  /** Default currency for this tenant */
  defaultCurrency: Currency;
  
  /** Base path for navigation (e.g., "/en" or "/site/{orgId}/en") */
  basePath: string;
  
  /** Whether we're in preview mode */
  isPreviewMode: boolean;
  
  /** Branding information for quick access */
  branding: {
    brandName: string;
    tagline?: string;
    logo?: string;
    logoSmall?: string;
    favicon?: string;
    faviconSvg?: string;
    appleTouchIcon?: string;
    mobileAppIcon?: string;
    splashScreen?: string;
    ogImage?: string;
    /** Brand colors from Organisation branding config */
    primaryColor?: string;
    secondaryColor?: string;
    accentColor?: string;
    backgroundColor?: string;
    /** Font families */
    primaryFont?: string;
    secondaryFont?: string;
    /** Theme mode preference */
    themeMode?: 'LIGHT' | 'DARK' | 'AUTO';
    /** PWA manifest configuration */
    pwa?: {
      name?: string;
      shortName?: string;
      description?: string;
      themeColor?: string;
      backgroundColor?: string;
      display?: 'standalone' | 'fullscreen' | 'minimal-ui' | 'browser';
      orientation?: 'portrait' | 'landscape' | 'any';
      startUrl?: string;
      scope?: string;
      icons?: Array<{
        src: string;
        sizes: string;
        type: string;
        purpose?: 'any' | 'maskable' | 'any maskable';
      }>;
    };
  };

  /** Analytics configuration */
  analytics?: {
    enabled: boolean;
    keys: SiteConfig['analytics'] extends { keys: infer K } ? K : Record<string, string>;
    verification: Record<string, string>;
    consent: {
      required: boolean;
      provider?: string;
      cookiebotId?: string;
      onetrustId?: string;
    };
    debugMode: boolean;
  };
  
  /** Commerce channel ID (from channelIntegrationId in site config) */
  channelId: string | null;
  
  /** WhatsApp chat widget configuration */
  whatsapp?: WhatsAppConfig | undefined;

  /** Contact information */
  contact: {
    email?: string;
    phone?: string;
    address?: {
      street?: string;
      city?: string;
      postalCode?: string;
      country?: string;
    };
  };
  
  /** Translation messages */
  messages: Record<string, unknown>;
}

/**
 * Default context value (used when no provider is present)
 */
const defaultContextValue: TenantContextValue | null = null;

/**
 * Tenant Context
 */
export const TenantContext = createContext<TenantContextValue | null>(defaultContextValue);

/**
 * Props for TenantProvider
 */
export interface TenantProviderProps {
  children: ReactNode;
  value: TenantContextValue;
}

/**
 * Tenant Provider Component
 * 
 * Wraps the application with tenant context.
 * Should be used in the root layout of tenant pages.
 */
export function TenantProvider({ children, value }: TenantProviderProps) {
  // Memoize the context value to prevent unnecessary re-renders
  const memoizedValue = useMemo(() => value, [
    value.organisationId,
    value.siteId,
    value.themeId,
    value.locale,
    value.isPreviewMode,
    // Note: We intentionally don't include all nested objects in deps
    // to avoid re-renders on every fetch. The context value should be
    // stable for the duration of a page render.
  ]);

  return (
    <TenantContext.Provider value={memoizedValue}>
      {children}
    </TenantContext.Provider>
  );
}

/**
 * Hook to access tenant context
 * 
 * @throws Error if used outside of TenantProvider
 * @returns Tenant context value
 */
export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);
  
  if (!context) {
    throw new Error(
      'useTenant must be used within a TenantProvider. ' +
      'Make sure your component is wrapped with <TenantProvider>.'
    );
  }
  
  return context;
}

/**
 * Hook to optionally access tenant context
 * Returns null if not within a TenantProvider (doesn't throw)
 */
export function useTenantOptional(): TenantContextValue | null {
  return useContext(TenantContext);
}

/**
 * Hook to check if we're in a tenant context
 */
export function useIsTenantContext(): boolean {
  const context = useContext(TenantContext);
  return context !== null;
}

/**
 * Hook to get the organisation ID (common use case)
 */
export function useOrganisationId(): string {
  const { organisationId } = useTenant();
  return organisationId;
}

/**
 * Hook to get the current locale
 */
export function useLocale(): Locale {
  const { locale } = useTenant();
  return locale;
}

/**
 * Hook to get branding information
 */
export function useBranding() {
  const { branding } = useTenant();
  return branding;
}

/**
 * Hook to get the base path for navigation
 */
export function useBasePath(): string {
  const { basePath } = useTenant();
  return basePath;
}

/**
 * Hook to check if we're in preview mode
 */
export function useIsPreviewMode(): boolean {
  const { isPreviewMode } = useTenant();
  return isPreviewMode;
}

/**
 * Hook to get translation messages
 */
export function useMessages(): Record<string, unknown> {
  const { messages } = useTenant();
  return messages;
}

/**
 * Hook to get the theme
 */
export function useTheme(): ThemeConfig {
  const { theme } = useTenant();
  return theme;
}

/**
 * Hook to get site configuration
 */
export function useSiteConfig(): SiteConfig {
  const { siteConfig } = useTenant();
  return siteConfig;
}

/**
 * Hook to get analytics configuration
 */
export function useAnalytics(): TenantContextValue['analytics'] {
  const { analytics } = useTenant();
  return analytics;
}

/**
 * Hook to check if analytics is enabled
 */
export function useIsAnalyticsEnabled(): boolean {
  const { analytics } = useTenant();
  return analytics?.enabled ?? false;
}

/**
 * Hook to get WhatsApp widget configuration
 */
export function useWhatsApp(): WhatsAppConfig | undefined {
  const { whatsapp } = useTenant();
  return whatsapp;
}
